import React, { useCallback, useEffect, useState } from 'react';
import { listUnifiedAgents, type UnifiedAiAgentDTO } from '../services/UnifiedAIAgentService';
import SessionService from '../services/SessionService';
import {
  listRules,
  previewRule,
  saveRule,
  type ProspectLevel,
  type ProspectPreview,
  type ProspectRule,
} from '../services/ProspectingService';

interface Props {
  sectorId: number;
  integrationId: string;
  integrationName: string;
  isOfficial: boolean;
  /** País padrão do telefone da integração (ISO). Vazio: leads com número sem "+" não são contatados. */
  phoneCountry: string;
  /** Tipo de fonte da integração (google_ads, site_form, other). Define o padrão de "Primeira mensagem" de uma regra nova. */
  sourceType?: string | null;
}

const DAY_LABELS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const DAY_NAMES = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const DEFAULT_DAYS = [1, 2, 3, 4, 5, 6];

const LEVELS: { value: ProspectLevel; label: string }[] = [
  { value: 'agir_avisar', label: 'Enviar e me avisar' },
  { value: 'aprovar_antes', label: 'Pedir minha aprovação antes' },
  { value: 'autonoma', label: 'Enviar sem avisar' },
];

type Draft = {
  enabled: boolean;
  agentId: string;
  script: string;
  level: ProspectLevel;
  days: number[];
  open: string;
  close: string;
  dailyCap: string;
  useFallback: boolean;
  fallbackText: string;
};

/**
 * Site e terceiros mandam texto livre que o lead (ou qualquer um) escreveu: começam pedindo aprovação.
 * Google Ads (formulário do anúncio) começa enviando e avisando.
 */
export function defaultLevelFor(sourceType?: string | null): ProspectLevel {
  return sourceType === 'site_form' || sourceType === 'other' ? 'aprovar_antes' : 'agir_avisar';
}

const emptyDraft = (sourceType?: string | null): Draft => ({
  enabled: false,
  agentId: '',
  script: '',
  level: defaultLevelFor(sourceType),
  days: DEFAULT_DAYS,
  open: '08:00',
  close: '20:00',
  dailyCap: '30',
  useFallback: false,
  fallbackText: '',
});

function draftFromRule(rule: ProspectRule): Draft {
  return {
    enabled: rule.enabled,
    agentId: String(rule.agentId),
    script: rule.script ?? '',
    level: rule.firstSendLevel,
    days: rule.window?.days ?? DEFAULT_DAYS,
    open: rule.window?.open ?? '08:00',
    close: rule.window?.close ?? '20:00',
    dailyCap: String(rule.dailyCap),
    useFallback: rule.useFallbackText,
    fallbackText: rule.fallbackText ?? '',
  };
}

/** "de segunda a sábado", "todos os dias" ou a lista dos dias. */
export function describeDays(days: number[]): string {
  const sorted = Array.from(new Set(days)).sort((a, b) => a - b);
  if (sorted.length === 7) return 'todos os dias';
  if (sorted.join(',') === DEFAULT_DAYS.join(',')) return 'de segunda a sábado';
  return sorted.map(d => DAY_NAMES[d]).join(', ');
}

/** Contato automático pelo WhatsApp: a regra de primeiro contato de uma integração de formulário. */
const ProspectRuleSection: React.FC<Props> = ({ sectorId, integrationId, integrationName, isOfficial, phoneCountry, sourceType }) => {
  const [agents, setAgents] = useState<UnifiedAiAgentDTO[]>([]);
  const [rule, setRule] = useState<ProspectRule | null>(null);
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(sourceType));
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<ProspectPreview | null>(null);

  const load = useCallback(async () => {
    setError('');
    setNotice('');
    setPreview(null);
    try {
      const [rules, list] = await Promise.all([
        listRules(sectorId),
        listUnifiedAgents(sectorId, SessionService.getToken() ?? '', 'campaign').catch(() => [] as UnifiedAiAgentDTO[]),
      ]);
      setAgents((list ?? []).filter(a => a.isEnabled));
      const found = (rules ?? []).find(r => r.sourceRef === integrationId) ?? null;
      setRule(found);
      setDraft(found ? draftFromRule(found) : emptyDraft(sourceType));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível carregar o contato automático.');
    }
  }, [sectorId, integrationId, sourceType]);

  useEffect(() => {
    if (!isOfficial) void load();
  }, [isOfficial, load]);

  const set = (patch: Partial<Draft>) => setDraft(current => ({ ...current, ...patch }));
  const toggleDay = (day: number) =>
    set({ days: draft.days.includes(day) ? draft.days.filter(d => d !== day) : [...draft.days, day].sort((a, b) => a - b) });

  const validate = (): string | null => {
    if (!draft.agentId) return 'Escolha o funcionário digital que vai fazer o contato.';
    if (draft.script.length > 1500) return 'O roteiro pode ter até 1500 caracteres.';
    const cap = Number(draft.dailyCap);
    if (!Number.isInteger(cap) || cap < 1 || cap > 200) return 'O limite diário deve ficar entre 1 e 200.';
    if (draft.days.length === 0) return 'Escolha ao menos um dia de envio.';
    if (draft.close <= draft.open) return 'O horário de fim deve ser depois do início.';
    if (draft.fallbackText.length > 600) return 'O texto fixo pode ter até 600 caracteres.';
    if (draft.useFallback && !draft.fallbackText.trim()) return 'Escreva o texto fixo ou desligue essa opção.';
    return null;
  };

  const onSave = async () => {
    const invalid = validate();
    if (invalid) {
      setError(invalid);
      setNotice('');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const saved = await saveRule({
        sectorId,
        name: `Contato automático: ${integrationName}`.slice(0, 120),
        enabled: draft.enabled,
        sourceKind: 'lead_integration',
        sourceRef: integrationId,
        agentId: Number(draft.agentId),
        script: draft.script.trim(),
        firstSendLevel: draft.level,
        fallbackText: draft.fallbackText.trim() ? draft.fallbackText.trim() : null,
        useFallbackText: draft.useFallback,
        window: { days: draft.days, open: draft.open, close: draft.close },
        dailyCap: Number(draft.dailyCap),
        maxLeadAgeHours: rule?.maxLeadAgeHours ?? 72,
      });
      setRule(saved);
      setNotice(draft.enabled ? 'Salvo. O contato automático está ligado.' : 'Salvo. O contato automático está desligado.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível salvar.');
    } finally {
      setSaving(false);
    }
  };

  const onPreview = async () => {
    setPreviewing(true);
    setError('');
    try {
      setPreview(await previewRule(sectorId, integrationId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Não foi possível gerar o exemplo.');
    } finally {
      setPreviewing(false);
    }
  };

  const agentName = agents.find(a => String(a.id) === draft.agentId)?.name;

  return (
    <section className="google-ads-prospect" aria-label="Contato automático pelo WhatsApp">
      <h2>Contato automático pelo WhatsApp</h2>
      <p className="google-ads-hint">
        Quando um lead novo chegar por esta integração, o funcionário escolhido escreve para ele usando as respostas do formulário.
      </p>

      {isOfficial ? (
        <p className="google-ads-hint">Disponível por enquanto só para WhatsApp não oficial.</p>
      ) : (
        <>
          {!phoneCountry ? (
            <p className="google-ads-inline-error">
              Sem país do telefone: leads com número sem + não serão contatados. Escolha o país acima.
            </p>
          ) : null}

          <label className="google-ads-check">
            <input type="checkbox" checked={draft.enabled} onChange={e => set({ enabled: e.target.checked })} />
            Entrar em contato automaticamente
          </label>

          <div className="google-ads-field">
            <label htmlFor="prospect-agent">Funcionário digital</label>
            <select id="prospect-agent" value={draft.agentId} onChange={e => set({ agentId: e.target.value })}>
              <option value="">Escolha um funcionário de campanha</option>
              {agents.map(a => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </div>

          <div className="google-ads-field">
            <label htmlFor="prospect-script">O que o funcionário deve fazer</label>
            <textarea
              id="prospect-script"
              rows={3}
              maxLength={1500}
              value={draft.script}
              placeholder="Ex.: apresente a visita técnica gratuita e ofereça dois horários nesta semana."
              onChange={e => set({ script: e.target.value })}
            />
          </div>

          <fieldset className="google-ads-fields">
            <legend>Primeira mensagem</legend>
            {LEVELS.map(l => (
              <label key={l.value} className="google-ads-check">
                <input type="radio" name="prospect-level" checked={draft.level === l.value} onChange={() => set({ level: l.value })} />
                {l.label}
              </label>
            ))}
          </fieldset>

          <fieldset className="google-ads-fields">
            <legend>Dias</legend>
            {DAY_LABELS.map((label, day) => (
              <label key={label} className="google-ads-check google-ads-check--inline">
                <input type="checkbox" checked={draft.days.includes(day)} onChange={() => toggleDay(day)} />
                {label}
              </label>
            ))}
            <div className="google-ads-field">
              <label htmlFor="prospect-open">Das</label>
              <input id="prospect-open" type="time" value={draft.open} onChange={e => set({ open: e.target.value })} />
            </div>
            <div className="google-ads-field">
              <label htmlFor="prospect-close">às</label>
              <input id="prospect-close" type="time" value={draft.close} onChange={e => set({ close: e.target.value })} />
            </div>
          </fieldset>

          <div className="google-ads-field">
            <label htmlFor="prospect-cap">Máximo de primeiros contatos por dia</label>
            <input
              id="prospect-cap"
              type="number"
              min={1}
              max={200}
              value={draft.dailyCap}
              onChange={e => set({ dailyCap: e.target.value })}
            />
          </div>

          <label className="google-ads-check">
            <input type="checkbox" checked={draft.useFallback} onChange={e => set({ useFallback: e.target.checked })} />
            Se a IA não conseguir escrever, enviar este texto fixo
          </label>
          {draft.useFallback ? (
            <div className="google-ads-field">
              <label htmlFor="prospect-fallback">Texto fixo</label>
              <textarea
                id="prospect-fallback"
                rows={3}
                maxLength={600}
                value={draft.fallbackText}
                onChange={e => set({ fallbackText: e.target.value })}
              />
              <p className="google-ads-hint">Use {'{primeiro_nome}'} para o nome do lead.</p>
            </div>
          ) : null}
          <p className="google-ads-hint">Desligado: o lead fica marcado para a equipe e nada é enviado.</p>

          {error ? <p className="google-ads-banner is-error" role="alert">{error}</p> : null}
          {notice ? <p className="google-ads-banner" role="status">{notice}</p> : null}
          {rule?.enabled && agentName ? (
            <p className="google-ads-hint">
              {`Ligado: ${agentName} escreve para cada lead novo desta integração, ${describeDays(rule.window.days)}, das ${rule.window.open} às ${rule.window.close}.`}
            </p>
          ) : null}

          <div className="google-ads-actions">
            <button type="button" className="google-ads-btn" onClick={() => void onSave()} disabled={saving}>
              {saving ? 'Salvando…' : 'Salvar'}
            </button>
            <button type="button" className="google-ads-btn is-secondary" onClick={() => void onPreview()} disabled={previewing || !rule}>
              {previewing ? 'Gerando…' : 'Ver exemplo'}
            </button>
          </div>
          {!rule ? <p className="google-ads-hint">Salve a regra para ver um exemplo da mensagem.</p> : null}

          {preview ? (
            <div className="google-ads-prospect-preview" role="status">
              {preview.text ? (
                <>
                  <blockquote>{preview.text}</blockquote>
                  {preview.usedFallback ? <span className="google-ads-hint">(texto fixo)</span> : null}
                </>
              ) : null}
              <p className="google-ads-hint">{preview.decision}</p>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export default ProspectRuleSection;
